import React, {
    useEffect,
    useState
} from "react";

import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
    Legend
} from "recharts";

import {
    FaBolt,
    FaIndustry,
    FaLeaf,
    FaChartLine
} from "react-icons/fa";

import apiClient
    from "../../shared/API/apiClient";

import carbonReportService
    from "../../services/carbonReportService";

import "../../styles/pages/reports.css";
import { useReportingYear } from "../../context/ReportingYearContext";

const COLORS = [
    "#2563eb",
    "#10b981",
    "#f59e0b",
    "#ef4444",
    "#8b5cf6"
];

const ICONS = {
    energy: FaBolt,
    scope1: FaIndustry,
    scope2: FaBolt,
    total: FaLeaf,
    intensity: FaChartLine
};
// ── sessionStorage helpers ─────────────────────────────────────────
const storageKey = (reportingYearId) =>
  `env_summary_entity_${reportingYearId}`;

const readPersistedEntity = (reportingYearId) => {
  if (!reportingYearId) return "";
  try {
    return sessionStorage.getItem(storageKey(reportingYearId)) || "";
  } catch {
    return "";
  }
};

const writePersistedEntity = (reportingYearId, entityId) => {
  if (!reportingYearId) return;
  try {
    if (entityId) {
      sessionStorage.setItem(storageKey(reportingYearId), entityId);
    } else {
      sessionStorage.removeItem(storageKey(reportingYearId));
    }
  } catch {
    // ignore
  }
};
const formatNumber = value => {

    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return "-";
    }

    return Number(value)
        .toLocaleString(
            "en-IN",
            {
                maximumFractionDigits: 4
            }
        );
};

const CarbonPerformancePage = () => {
    const { reportingYear, reportingYearId } =
    useReportingYear();

const [loading, setLoading] =
    useState(false);

const [report, setReport] =
    useState(null);

const [reportingYears, setReportingYears] =
    useState([]);

const [legalEntities, setLegalEntities] =
    useState([]);

const [selectedEntity, setSelectedEntity] =
    useState(() =>
        readPersistedEntity(reportingYearId)
    );

const [reportingYearInfo, setReportingYearInfo] =
    useState(null);

const handleEntityChange = (entityId) => {
    setSelectedEntity(entityId);
    writePersistedEntity(
        reportingYearId,
        entityId
    );
};

const [filters, setFilters] = useState({
    reporting_year_id: reportingYearId,
    legal_entity_id: selectedEntity,
    filter_type: "THIS_YEAR",
    quarter: "",
    month: "",
    from_date: "",
    to_date: ""
});

  const handleFilterChange = (field, value) => {

    const updated = {
        ...filters,
        [field]: value
    };

    if(field === "filter_type") {

        updated.quarter = "";
        updated.month = "";

        if(value === "CUSTOM" && reportingYearInfo) {
            updated.from_date =
                reportingYearInfo.start_date;

            updated.to_date =
                reportingYearInfo.end_date;
        }

        if(value !== "CUSTOM") {
            updated.from_date = "";
            updated.to_date = "";
        }
    }

    setFilters(updated);
};
const getReportingMonths = () => {

    if (!reportingYearInfo) {
        return [];
    }

    const months = [];

    const start =
        new Date(
            reportingYearInfo.start_date
        );

    const reportingEnd =
        new Date(
            reportingYearInfo.end_date
        );

    const today =
        new Date();

    // Use whichever comes first:
    const end =
        today < reportingEnd
            ? today
            : reportingEnd;

    let year =
        start.getFullYear();

    let month =
        start.getMonth();

    while (
        year < end.getFullYear() ||
        (
            year === end.getFullYear() &&
            month <= end.getMonth()
        )
    ) {

        const date =
            new Date(
                year,
                month,
                1
            );

        months.push({

            value:
                `${year}-${String(
                    month + 1
                ).padStart(
                    2,
                    "0"
                )}`,

            label:
                date.toLocaleDateString(
                    "en-IN",
                    {
                        month: "short",
                        year: "numeric"
                    }
                )
        });

        month++;

        if (month > 11) {

            month = 0;
            year++;
        }
    }

    return months;
};
    // =====================================================
    // LEGAL ENTITIES
    // =====================================================

    useEffect(() => {

        const loadEntities =
            async () => {

                if (
                    !reportingYearId
                ) {
                    return;
                }

                try {

                    const res =
                        await apiClient.get(
                            "/api/legal-entities-by-year",
                            {
                                params: {
                                    reporting_year_id:
                                        reportingYearId
                                }
                            }
                        );

                    setLegalEntities(
                        res.data.data || []
                    );

                } catch (e) {

                    console.error(e);

                    setLegalEntities([]);
                }
            };

        loadEntities();

    }, [reportingYearId]);

    // =====================================================
    // DASHBOARD
    // =====================================================
useEffect(() => {

   const loadReportingYear = async () => {

    try {

        const res =
            await apiClient.post(
                "/api/reportingyear"
            );

        if (res.data.code === 200) {

            const selected =
                (res.data.data || [])
                    .find(
                        y =>
                            Number(y.id) ===
                            Number(reportingYearId)
                    );

            setReportingYearInfo(
                selected || null
            );
        }

    } catch (e) {

        console.error(
            "Reporting year API error:",
            e
        );
    }
};

    loadReportingYear();

}, [reportingYearId]);
    const loadReport = async () => {

            if (!reportingYearId || !selectedEntity) {
                return;
            }
                try {

                    setLoading(true);

                    const { data } =
                        await carbonReportService.getDashboard({
                            reporting_year_id: reportingYearId,
                            legal_entity_id: selectedEntity,
                            filter_type: filters.filter_type,
                            quarter: filters.quarter,
                            month: filters.month,
                            from_date: filters.from_date,
                            to_date: filters.to_date
                        });

                    setReport(data);

                } catch (e) {

                    console.error(e);

                } finally {

                    setLoading(false);
                }
            };

    useEffect(() => {

            if (!selectedEntity)
                return;

            if (filters.filter_type === "THIS_YEAR") {

                loadReport();
            }

        }, [reportingYearId,selectedEntity]);

    // =====================================================
    // LOADER
    // =====================================================

    if (loading) {

        return (

            <div className="dash-loader-wrapper">

                <div className="dash-loader" />

                <div className="dash-loader-text">
                    Loading Carbon Performance...
                </div>

            </div>
        );
    }

    // =====================================================
    // PAGE
    // =====================================================

    return (

        <div className="dash-page">

            <div className="dash-page-title">
                Carbon Performance
            </div>

            <div className="dash-page-subtitle">
                Scope 1, Scope 2 and Carbon Footprint Analysis
            </div>

            {/* FILTERS */}

            <div className="filter-card">
    
                
        <div className="filter-group">
          <label>Legal Entity *</label>
          <select
            className="filter-fields"
            value={selectedEntity}
            onChange={(e) => handleEntityChange(e.target.value)}
          >
            <option value="">Select</option>
            {legalEntities.map((entity) => (
              <option key={entity.entity_id} value={entity.entity_id}>
                {entity.entity_name}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">

    <label>
        Period
    </label>

    <select
        className="filter-fields"
        value={filters.filter_type}
        onChange={(e) =>
            handleFilterChange(
                "filter_type",
                e.target.value
            )
        }
    >
        <option value="THIS_YEAR">This Year</option>
        <option value="THIS_QUARTER">Quarter</option>
        <option value="THIS_MONTH">Month</option>
        <option value="CUSTOM">Custom</option>
    </select>

    
    </div>
        
        {
    filters.filter_type === "THIS_QUARTER" && (
        <div className="filter-group">

            <label>
                Quarter
            </label>

            <select
                className="filter-fields"
                value={filters.quarter}
                onChange={(e) =>
                    handleFilterChange(
                        "quarter",
                        e.target.value
                    )
                }
            >
                <option value="">
                    Select
                </option>

                <option value="1">Q1</option>
                <option value="2">Q2</option>
                <option value="3">Q3</option>
                <option value="4">Q4</option>
            </select>

        </div>
    )
}
{
    filters.filter_type === "THIS_MONTH" && (

        <div className="filter-group">

            <label>
                Month
            </label>

            <select
                className="filter-fields"
                value={filters.month}
                onChange={(e) =>
                    handleFilterChange(
                        "month",
                        e.target.value
                    )
                }
            >

                <option value="">
                    Select Month
                </option>

                {
                    getReportingMonths().map(
                        month => (

                            <option
                                key={month.value}
                                value={month.value}
                            >
                                {month.label}
                            </option>
                        )
                    )
                }

            </select>

        </div>

    )

}
{
    filters.filter_type === "CUSTOM" && (
        <>
            <div className="filter-group">

                <label>
                    From
                </label>

                <input
                    type="date"
                    className="filter-fields"
                    min={
                        reportingYearInfo?.start_date
                    }
                    max={
                        reportingYearInfo?.end_date
                    }
                    value={filters.from_date}
                    onChange={(e) =>
                        handleFilterChange(
                            "from_date",
                            e.target.value
                        )
                    }
        />

            </div>

            <div className="filter-group">

                <label>
                    To
                </label>

                <input
                        type="date"
                        className="filter-fields"
                        min={
                            reportingYearInfo?.start_date
                        }
                        max={
                            reportingYearInfo?.end_date
                        }
                        value={filters.to_date}
                        onChange={(e) =>
                            handleFilterChange(
                                "to_date",
                                e.target.value
                            )
                        }
                    />

            </div>
        </>
    )
}
<div className="filter-group">

    <label>&nbsp;</label>

    <button
        className="org-btn-save"
        onClick={loadReport}
        disabled={!selectedEntity}>
        Apply
    </button>

</div>
        

        </div>
                
            {
                !report && (

                    <div className="empty-entity">
          <div className="empty-entity__icon"><i className="fa-solid fa-building" /></div>
          <div className="empty-entity__title">Select a Legal Entity to continue</div>
          <div className="empty-entity__sub">
            Choose a legal entity from the view Carbon Performance.
          </div>
          
          
        
        </div>
                )
            }

            {
                report && (() => {

                    const {
                        reporting_period,
                        applied_filter,
                        kpis,
                        trend,
                        trend_config,
                        scope_distribution,
                        location_contribution,
                        table,
                        features
                    } = report;
                    const locationData =
                    (location_contribution || [])
                        .map(item => ({
                            ...item,
                            value: Number(item.value)
                        }));

                const scopeData =
                    (scope_distribution || [])
                        .map(item => ({
                            ...item,
                            value: Number(item.value)
                        }));
                    return (

                        <>

                            <div className="reporting_year_line">

                            <strong>
                                {applied_filter?.label}
                            </strong>

                            {" | "}

                            {applied_filter?.from_date}

                            {" to "}

                            {applied_filter?.to_date}

                        </div>

                            {/* KPI */}

                            <div
                                className="dash-stat-grid"
                                style={{
                                    gridTemplateColumns:
                                        `repeat(${kpis.length},1fr)`
                                }}
                            >

                                {
                                    kpis.map(
                                        kpi => {

                                            const Icon =
                                                ICONS[
                                                    kpi.key
                                                ];

                                            return (

                                                <div
                                                    key={kpi.key}
                                                    className="dash-stat-card"
                                                >

                                                    <div className="dash-stat-header">

                                                        <Icon className="dash-stat-icon" />

                                                        <div className="dash-stat-label">
                                                            {
                                                                kpi.title
                                                            }
                                                        </div>

                                                    </div>

                                                    <div className="dash-stat-value">
                                                        {
                                                            formatNumber(
                                                                kpi.value
                                                            )
                                                        }
                                                    </div>

                                                    <div className="dash-stat-sub">
                                                        {
                                                            kpi.unit
                                                        }
                                                    </div>

                                                </div>
                                            );
                                        }
                                    )
                                }

                            </div>

                            {
                                features.show_trend && (

                                    <div className="chart-card">

                                        <h4>
                                            Monthly Carbon Trend
                                        </h4>

                                        <ResponsiveContainer
                                            width="100%"
                                            height={320}
                                        >

                                            <LineChart
                                                data={trend}
                                            >

                                                <CartesianGrid strokeDasharray="3 3" />

                                                <XAxis dataKey="period" />

                                                <YAxis />

                                                <Tooltip />

                                                <Legend verticalAlign="top" height={40}/>

                                                {
                                                    trend_config.map(
                                                        item => (
                                                            <Line
                                                                key={item.key}
                                                                type="monotone"
                                                                dataKey={item.key}
                                                                stroke={item.color}
                                                                name={item.label}
                                                            />
                                                        )
                                                    )
                                                }

                                            </LineChart>

                                        </ResponsiveContainer>

                                    </div>
                                )
                            }

                            <div className="chart-row">

                               {
    features.show_scope_distribution && (

        <div className="chart-card">

            <h4>
                Scope Distribution
            </h4>

            <ResponsiveContainer
                width="100%"
                height={300}
            >

                <BarChart
                    data={scope_distribution}
                >

                    <CartesianGrid
                        strokeDasharray="3 3"
                    />

                    <XAxis
                        dataKey="name"
                    />

                    <YAxis />

                    <Tooltip />

                    <Legend />

                    <Bar dataKey="value">
        {scope_distribution.map((entry, index) => (
            <Cell
                key={index}
                fill={
                    entry.name === "Scope 1"
                        ? "#ef4444"   // red
                        : "#2563eb"   // blue
                }
            />
        ))}
    </Bar>

                </BarChart>

            </ResponsiveContainer>

        </div>
    )
}

                                <div className="chart-card">

                                    <h4>
                                        Location Contribution
                                    </h4>

                                    {
    locationData.length > 1 ? (

        <ResponsiveContainer
            width="100%"
            height={300}
        >
            <PieChart>

                <Pie
                    data={locationData}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={90}
                    label
                >
                    {
                        locationData.map(
                            (item, index) => (
                                <Cell
                                    key={index}
                                    fill={
                                        COLORS[
                                            index %
                                            COLORS.length
                                        ]
                                    }
                                />
                            )
                        )
                    }
                </Pie>

                <Tooltip />

                <Legend />

            </PieChart>

        </ResponsiveContainer>

    ) : (

        <div
            className="dash-empty"
            style={{
                paddingTop: 70
            }}
        >

            <h2>
                100%
            </h2>

            <div>
                {
                    locationData[0]?.name ||
                    "No Data"
                }
            </div>

            <strong>
                {
                    formatNumber(
                        locationData[0]?.value
                    )
                }
                {" tCO₂e"}
            </strong>

        </div>

    )
}

                                </div>

                            </div>

                            {
                                features.show_table && (

                                    <div className="table-card">

                                        <h4>
                                            Carbon Performance Details
                                        </h4>

                                        <table className="table">

                                            <thead>

                                                <tr>

                                                    <th>Location</th>
                                                    <th>Scope 1</th>
                                                    <th>Scope 2</th>
                                                    <th>Total</th>
                                                    <th>Energy</th>
                                                    <th>Intensity</th>
                                                    <th>%</th>

                                                </tr>

                                            </thead>

                                            <tbody>

                                                {
                                                    table.map(
                                                        (
                                                            row,
                                                            index
                                                        ) => (

                                                            <tr
                                                                key={index}
                                                            >

                                                                <td>{row.location}</td>
                                                                <td>{formatNumber(row.scope1)}</td>
                                                                <td>{formatNumber(row.scope2)}</td>
                                                                <td>{formatNumber(row.total)}</td>
                                                                <td>{formatNumber(row.energy)}</td>
                                                                <td>{row.intensity}</td>
                                                                <td>{Number(row.contribution).toFixed(2)}%</td>

                                                            </tr>
                                                        )
                                                    )
                                                }

                                            </tbody>

                                        </table>

                                    </div>
                                )
                            }

                        </>
                    );
                })()
            }

        </div>
    );
};

export default CarbonPerformancePage;